import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Form, Input, Row } from "antd";
import PhoneInput from "antd-phone-input";
import { set } from "lodash";

const PhoneNumberInput = ({
  locationType = null,
  onlyCountries = undefined,
  excludeCountries,
  formItemProps: _formItemProps,
  showExtensionInput = true,
  borderRadius = 4,
  // onChange,
  disabled = false,
  readOnly = false,
  nextInputRef = undefined,
  ...params
}) => {
  const [extensionValue, setExtensionValue] = useState("");
  const extensionInputRef = useRef();
  const phoneInputWrapperRef = useRef();

  const formItemProps = useMemo(() => {
    const newParams = { ..._formItemProps };
    let { rules } = newParams;

    if (!rules) {
      rules = [];
    }

    const validatorFn = async (_, { valid }) => {
      const isRequired = rules.some(rule => rule.required);

      if (!isRequired) {
        return Promise.resolve();
      }

      // pass true to enable strict validation, otherwise it will allow invalid phone numbers like +1 (111) 222 3333
      if (valid()) {
        return Promise.resolve();
      }

      const label = newParams?.label ? `Please enter a ${newParams?.label}` : "Invalid phone number";
      return Promise.reject(label);
    };

    rules.push({ validator: validatorFn });
    newParams.rules = rules;

    return newParams;
  }, [_formItemProps]);

  const phoneInputName = useMemo(() => {
    const { name } = formItemProps || {};

    if (locationType) {
      return [locationType, name];
    }

    return name;
  }, [locationType, formItemProps]);

  // use as key to force re-render when the value changes since we're updating the value through the form ref
  const currentValue = Form.useWatch(phoneInputName, formItemProps?.formRef);

  const extensionInputName = useMemo(() => {
    const defaultName = "phoneExtension";

    if (!formItemProps) {
      return defaultName;
    }

    const { name, extensionName } = formItemProps || {};

    if (!name && !extensionName) {
      return defaultName;
    }

    const extensionNameNotNested = extensionName || `${name}Extension`;

    // If nesting level provided in form (e.g. "Pickup", "Dropoff", etc)
    if (locationType) {
      return [locationType, extensionNameNotNested];
    }

    return extensionNameNotNested;
  }, [formItemProps, locationType]);

  // Disable tab index on country selector dropdown
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is fully rendered
    const frameId = requestAnimationFrame(() => {
      if (phoneInputWrapperRef.current) {
        // The actual focusable element is the hidden search input inside the Select
        const searchInput = phoneInputWrapperRef.current.querySelector(
          ".ant-input-group-addon .ant-select-selection-search-input",
        );
        if (searchInput) {
          searchInput.setAttribute("tabindex", "-1");
        }
      }
    });

    return () => cancelAnimationFrame(frameId);
  }, []);

  // Add default country code of 1 for pasted phone numbers
  useEffect(() => {
    const handlePaste = e => {
      const targetClassName = Array.isArray(phoneInputName) ? phoneInputName.join(",") : phoneInputName;
      const isTargetInput = e.target.parentNode.parentNode.classList.contains(`${targetClassName}-phone-input`);
      if (!isTargetInput) return;

      const pastedText = e.clipboardData.getData("text");
      const pastedTextCleaned = pastedText
        .trim()
        .replaceAll("(", "")
        .replaceAll(")", "")
        .replaceAll("+", "")
        .replaceAll(".", "")
        .replaceAll("-", "")
        .replaceAll(" ", "");

      let updatedInputValue;

      if (pastedTextCleaned.startsWith("1")) {
        updatedInputValue = `1 ${pastedTextCleaned.slice(1)}`;
      } else {
        updatedInputValue = `1 ${pastedTextCleaned}`;
      }

      const newValues = {};

      set(newValues, phoneInputName, updatedInputValue);

      const setFieldsValue = formItemProps?.formRef?.current?.setFieldsValue ?? formItemProps?.formRef?.setFieldsValue;
      setFieldsValue?.(newValues);
      e.preventDefault();
    };

    window.addEventListener("paste", handlePaste);

    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, [phoneInputName, formItemProps]);

  const handleKeyDown = e => {
    if (!nextInputRef) return;

    if (e.key === "Tab") {
      e.preventDefault();
      nextInputRef.current.focus();
    }
  };

  const handlePhoneInputFocus = e => {
    const input = e.target;

    // Move cursor to the end of the input value
    requestAnimationFrame(() => {
      if (input && input.value) {
        const len = input.value.length;
        input.setSelectionRange(len, len);
      }
    });
  };

  const phoneInputClassName = useMemo(() => {
    const prefix = readOnly ? "phone-input-readonly" : "";

    if (showExtensionInput) {
      return `${prefix} tel-input-with-extension custom-phone-input`;
    }

    let name = phoneInputName;
    if (Array.isArray(phoneInputName)) {
      name = phoneInputName.join("-");
    }

    return `${prefix} ${name}-phone-input custom-phone-input`;
  }, [phoneInputName, showExtensionInput, readOnly]);

  return (
    <Row wrap={false} style={{ width: "100%" }}>
      <Col span={showExtensionInput ? 19 : 24} ref={phoneInputWrapperRef}>
        <Form.Item
          style={{
            flex: 1,
            ...formItemProps?.style,
          }}
          name={phoneInputName}
          label={
            <label htmlFor={phoneInputName} style={{ maxHeight: "20px" }}>
              {formItemProps?.label}
            </label>
          }
          rules={formItemProps?.rules}
          validateTrigger={formItemProps?.validateTrigger}
        >
          <PhoneInput
            {...params}
            enableSearch
            onlyCountries={onlyCountries}
            excludeCountries={excludeCountries}
            preferredCountries={["us", "ca", "mx"]}
            country="us"
            readOnly={readOnly}
            disableDropdown={readOnly}
            disabled={disabled}
            className={phoneInputClassName}
            style={{
              borderRadius,
              marginTop: "1px",
            }}
            autoComplete="tel"
            type="tel"
            key={currentValue}
            onFocus={handlePhoneInputFocus}
          />
        </Form.Item>
      </Col>

      {showExtensionInput && (
        <Col span={5}>
          <Form.Item
            label={
              <label htmlFor={extensionInputName} style={{ maxHeight: "20px" }}>
                {formItemProps?.extensionLabel ?? "Extension"}
              </label>
            }
            name={extensionInputName}
            style={{ flex: 1 }}
          >
            <Input
              disabled={disabled}
              ref={extensionInputRef}
              style={{
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                borderLeft: "none",
                height: "32px",
              }}
              value={extensionValue}
              onChange={e => {
                const digitsOnly = e.target.value.replace(/\D/g, "");
                setExtensionValue(digitsOnly);
                const newValues = {};
                set(newValues, extensionInputName, digitsOnly);
                const setFieldsValue =
                  formItemProps?.formRef?.current?.setFieldsValue ?? formItemProps?.formRef?.setFieldsValue;
                setFieldsValue?.(newValues);
              }}
              autoComplete="nope" // Set to ensure chrome autofill ignores this input
              type="text" // Set to ensure chrome autofill does not mistake for intended phone input
              id="extensionInput" // Cannot have "phone" in it or it will break chrome autofill
              onKeyDown={handleKeyDown}
            />
          </Form.Item>
        </Col>
      )}
    </Row>
  );
};

PhoneNumberInput.defaultProps = {
  showExtensionInput: true,
  onlyCountries: undefined,
  excludeCountries: undefined,
  borderRadius: 8,
  disabled: false,
  nextInputRef: undefined,
  locationType: undefined,
  formItemProps: undefined,
  onChange: undefined,
};

export default PhoneNumberInput;
